import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Download, Search, TrendingUp, LogOut, Music, Film, ShoppingBag, Palette } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import '../styles/AdminDashboard.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [fans, setFans] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('mwp_admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    // Set axios default headers
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [fansResponse, statsResponse] = await Promise.all([
        axios.get(`${API}/fans/`),
        axios.get(`${API}/fans/stats`)
      ]);
      
      setFans(fansResponse.data);
      setStats(statsResponse.data);
    } catch (error) {
      console.error('Error loading data:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        handleLogout();
      } else {
        toast.error('Failed to load data');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadData();
      return;
    }

    try {
      const response = await axios.get(`${API}/fans/search?query=${searchQuery}`);
      setFans(response.data);
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Search failed');
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/fans/export/csv`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'mwp_fans.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Fans exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export failed');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('mwp_admin_token');
    delete axios.defaults.headers.common['Authorization'];
    navigate('/admin/login');
  };

  if (isLoading) {
    return (
      <div className="admin-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <img 
            src="https://customer-assets.emergentagent.com/job_daa53b2c-a32e-47fc-b8be-c3729304beb0/artifacts/d3h4utx6_MWP%20Label%20Logo.png" 
            alt="MWP" 
            className="dashboard-logo"
          />
          <h1>MWP ADMIN DASHBOARD</h1>
        </div>
        <button onClick={handleLogout} className="logout-btn">
          <LogOut size={20} />
          Logout
        </button>
      </div>

      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <Users size={32} />
            </div>
            <div className="stat-content">
              <h3>Total Fans</h3>
              <p className="stat-number">{stats.total_fans}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <Music size={32} />
            </div>
            <div className="stat-content">
              <h3>Music Fans</h3>
              <p className="stat-number">{stats.interests.music}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <Film size={32} />
            </div>
            <div className="stat-content">
              <h3>Film Fans</h3>
              <p className="stat-number">{stats.interests.film}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <Palette size={32} />
            </div>
            <div className="stat-content">
              <h3>Art Fans</h3>
              <p className="stat-number">{stats.interests.art}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <ShoppingBag size={32} />
            </div>
            <div className="stat-content">
              <h3>Merch Interest</h3>
              <p className="stat-number">{stats.interests.merchandise}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <TrendingUp size={32} />
            </div>
            <div className="stat-content">
              <h3>Growth</h3>
              <p className="stat-number">+{stats.total_fans}</p>
            </div>
          </div>
        </div>
      )}

      <div className="fans-section">
        <div className="section-header">
          <h2>Fan Database</h2>
          <div className="header-actions">
            <div className="search-box">
              <Search size={20} />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <button onClick={handleSearch} className="search-btn">Search</button>
            </div>
            <button onClick={handleExportCSV} className="export-btn">
              <Download size={20} />
              Export CSV
            </button>
          </div>
        </div>

        <div className="fans-table-container">
          <table className="fans-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Country</th>
                <th>Interests</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {fans.map((fan) => (
                <tr key={fan.id}>
                  <td>{fan.name}</td>
                  <td>{fan.email}</td>
                  <td>{fan.country}</td>
                  <td>
                    <div className="interests-badges">
                      {fan.interests.music && <span className="badge">Music</span>}
                      {fan.interests.film && <span className="badge">Film</span>}
                      {fan.interests.art && <span className="badge">Art</span>}
                      {fan.interests.merchandise && <span className="badge">Merch</span>}
                    </div>
                  </td>
                  <td>{new Date(fan.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {fans.length === 0 && (
            <div className="empty-state">
              <Users size={48} />
              <p>No fans found</p>
            </div>
          )}
        </div>
      </div>

      {stats && stats.top_countries && stats.top_countries.length > 0 && (
        <div className="countries-section">
          <h2>Top Countries</h2>
          <div className="countries-grid">
            {stats.top_countries.map((country, index) => (
              <div key={index} className="country-card">
                <h3>{country._id}</h3>
                <p>{country.count} fans</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
